import { signal, effect } from '@preact/signals';
import { migrateLook } from '../game/input/look';

export interface ClientSettings {
  /** Look speeds (1 = the original feel): horizontal and vertical, normal and while aiming down a sight (see game/input/look.ts). */
  lookX: number;
  lookY: number;
  adsX: number;
  adsY: number;
  /** Ask the browser for unaccelerated mouse movement (ignores operating-system mouse acceleration) when it can. */
  rawMouse: boolean;
  invertY: boolean;
  quality: 'auto' | 'low' | 'medium' | 'high';
  masterVolume: number;
  sfxVolume: number;
  musicVolume: number;
  showFps: boolean;
  touchControls: 'auto' | 'on' | 'off';
  autoFire: boolean;
  aimAssist: boolean;
  crosshairColor: string;
  crosshairStyle: 'cross' | 'dot' | 'circle';
  touchScale: number;
  language: string;
  /** Show the lobby chat drawer (parents can turn chat off entirely). */
  chatEnabled: boolean;
  /** Tone down menu animations. */
  reduceMotion: boolean;
  /** Phone browsers: go fullscreen (and landscape) when a match starts. */
  autoFullscreen: boolean;
  /** Keyboard rebinding overrides (action -> KeyboardEvent.code). */
  keybinds: Partial<Record<import('../game/input/Input').BindAction, string>>;
}

const DEFAULTS: ClientSettings = {
  lookX: 1,
  lookY: 1,
  adsX: 1,
  adsY: 1,
  rawMouse: true,
  invertY: false,
  quality: 'auto',
  masterVolume: 0.8,
  sfxVolume: 1,
  musicVolume: 0.45,
  showFps: false,
  touchControls: 'auto',
  autoFire: false,
  aimAssist: true,
  crosshairColor: '#ffffff',
  crosshairStyle: 'cross',
  touchScale: 1,
  language: 'en',
  chatEnabled: true,
  reduceMotion: false,
  autoFullscreen: true,
  keybinds: {},
};

function load(): ClientSettings {
  try {
    const raw = localStorage.getItem('vm.settings');
    if (raw) return { ...DEFAULTS, ...(migrateLook(JSON.parse(raw) as Record<string, unknown>) as Partial<ClientSettings>) };
  } catch {
    /* storage unavailable */
  }
  return { ...DEFAULTS };
}

export const settings = signal<ClientSettings>(load());

effect(() => {
  const s = settings.value;
  try {
    localStorage.setItem('vm.settings', JSON.stringify(s));
  } catch {
    /* storage unavailable */
  }
});

export function updateSettings(patch: Partial<ClientSettings>): void {
  settings.value = { ...settings.value, ...patch };
}

export const isTouchDevice = (): boolean => typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0) && window.matchMedia?.('(pointer: coarse)').matches;

export function useTouchControls(): boolean {
  const t = settings.value.touchControls;
  return t === 'on' || (t === 'auto' && isTouchDevice());
}

/** Picks a render quality for 'auto' from device hints. */
export function autoQuality(): 'low' | 'medium' | 'high' {
  if (isTouchDevice()) return (navigator.hardwareConcurrency ?? 4) >= 8 ? 'medium' : 'low';
  return 'high';
}
